export const MultiTermsNewContent = {
  pageTitle: `Review and sign new account agreements and authorize transfer of assets`,
  taxCertificationStatementsCheckboxLabel: `Tax Certification: By checking this box, I certify and sign under penalty of perjury that the tax certification statements appearing above are true.`,
  signAndAgreeCheckboxLabel: `I sign and agree`,
  submitButton: `Submit application`,
  errorMsg: `This field is required.`,
  loadingSpinner: `We're preparing your request... <br> please wait a few minutes`,
  internalHeader: `Crew cannot open new accounts on behalf of clients.`,
  internalDescription: `Please nudge clients eligible for CXD off the call to self submit.`,
  aboutBrokerage: {
    header: `About opening your new brokerage account(s)`,
    sections: [
      {
        content: [
          `You are opening one or more non-retirement and/or retirement brokerage accounts, as identified in this application, with Vanguard Brokerage Services.`,
        ],
      },
      {
        header: `Review our Form CRS`,
        content: [
          `Please read our Form CRS presented below to help you better understand the services, fees, costs, conflicts, and conduct standards that apply to your relationship with Vanguard Brokerage Services.`,
          `<a class="link c11n-link c11n-link--bold c11n-link--reinforced" href="https://personal.vanguard.com/pdf/vmccrs.pdf" target="_blank" rel="noopener noreferrer"><b>Form CRS (PDF)</b></a>`,
        ],
      },
    ],
  },
  consentElectronicDelivery: {
    header: `Consent to electronic delivery`,
    sections: [
      {
        content: [
          `How to access online documents:`,
          `To view and print the documents listed below, you’ll need software that reads PDF files. If you can’t obtain the documents, or you don’t consent to electronic delivery, you can’t submit an application through this process. Contact us to request a free copy of these documents. Vanguard won’t charge you for using our website (but you could incur expenses from your service provider). Internet service providers may occasionally experience system failures, and hyperlinks may fail to function properly.`,
        ],
      },
      {
        content: [
          `Duration of electronic consent: `,
          `This election and consent applies only to the documents required to complete this specific transaction.`,
          `By clicking <b>I sign and agree</b> below, you consent to receive the Form CRS above and the following documents electronically and acknowledge having reviewed, printed, or otherwise received them.`,
        ],
        links: {
          allAccounts: [
            {
              text: `Vanguard Brokerage Account Agreement (PDF)`,
              link: `https://personal1.vanguard.com/pdf/vbsaacp.pdf`,
            },
            {
              text: `Vanguard’s Privacy Policy (Website)`,
              link: `https://investor.vanguard.com/investing/privacy-policy`,
            },
            {
              text: `Vanguard Brokerage Services commission and fee schedules (PDF)`,
              link: `https://personal1.vanguard.com/pdf/v414.pdf`,
            },
            {
              text: `Business Contingency Planning and Disaster Recovery Programs at Vanguard (PDF)`,
              link: `http://www.vanguard.com/pdf/s845.pdf`,
            },
          ],
        },
      },
      {
        content: [
          `Settlement fund documents (as applicable)`,
          `<a class="link-underline c11n-link--secondary" href="https://personal.vanguard.com/pub/Pdf/sp33.pdf" target="_blank" rel="noopener noreferrer">Vanguard Federal Money Market Fund (PDF)</a><br>
          You can also review the fund’s prospectus at the bottom of your confirmation page once completing this application.`,
        ],
      },
      {
        content: [`Documents applicable to retirement accounts only:`],
        links: {
          IRALinks: [
            {
              text: `The Vanguard Traditional IRA, SEP-IRA, and Roth IRA Disclosure Statement and Custodial Account Agreement (PDF)`,
              link: `https://personal1.vanguard.com/pdf/s831.pdf`,
            },
          ],
        },
      },
    ],
  },
  authorizationNewIRA: {
    header: `Authorization of new IRA owner`,
    sections: [
      {
        content: [
          `If You were designated as a beneficiary by relationship (i.e.,spouse) rather than by name, You understand that You also must indicate consent to the following by signing:`,
        ],
        termsList: {
          list: [
            `You were legally married to the decedent at the time of his or her death, and as of his or her date of death, no final decree of divorce as to this marriage was in effect in any jurisdiction.`,
            `To the best of Your knowledge, there are no other persons who are entitled to claim a share of the decedent’s Vanguard accounts as the decedent’s surviving spouse.`,
            `You understand that Vanguard is relying on the information You are providing in this Application. You confirm that, to the best of Your knowledge, the information You have provided is true and correct as of the date hereof. You agree to indemnify and hold The Vanguard Group, Inc., Vanguard Marketing Corporation, their affiliates, and each of the investment company members of The Vanguard Group and their respective officers, employees, and agents (collectively, “Vanguard”) harmless from any liability, cost, or damage of any kind that Vanguard incurs for relying in good faith upon the information provided by You in this Application.`,
          ],
        },
      },
    ],
  },
  taxCertification: {
    header: `Tax certification`,
    sections: [
      {
        content: [
          `If I am a U.S. citizen, a U.S. resident alien, or a representative of a U.S. entity, I certify under penalty of perjury that:`,
        ],
        certificate: {
          list: [
            `The number shown on this form is my correct taxpayer identification number (or I am waiting for a number to be issued to me); and`,
            `I am not subject to backup withholding because: (a) I am exempt from backup withholding, or (b) I have not been notified by the Internal Revenue Service (IRS) that I am subject to backup withholding as a result of a failure to report all interest or dividends, or (c) the IRS has notified me that I am no longer subject to backup withholding; and </p>
              <p>Important:  If you have been notified by the IRS that you are currently subject to backup withholding because you have failed to report all interest or dividends on your tax return, you are not eligible to submit this application using an electronic signature. You must <a class="link-underline c11n-link c11n-link--bold c11n-link--reinforced" href="https://support.vanguard.com/">click here to exit this application</a> and contact Vanguard for a paper application.`,
            `I am a U.S. citizen or other U.S. person (as defined by the Internal Revenue Service in its <a class="link-underline c11n-link c11n-link--bold c11n-link--secondary" href="https://www.irs.gov/forms-pubs/about-form-w-9" target="_blank">W-9 instructions</a>); and `,
            'The Foreign Account Tax Compliance Act (FATCA) code(s) entered on this form (if any) indicating that I am exempt from FATCA reporting is correct.',
          ],
        },
      },
    ],
  },
  authorizationToOpenAccount: {
    header: `Authorization to open a new brokerage account(s) and transfer assets`,
    sections: [
      {
        content: [
          `By clicking <b>I sign and agree</b> and <b>Submit application</b>, I authorize the opening of the new account(s) identified in this application. I also acknowledge that I have read and accept the terms above and the <a class="link c11n-link c11n-link--bold c11n-link--reinforced" href="http://www.vanguard.com/pdf/vbsaacp.pdf" target="_blank">Vanguard Brokerage Account Agreement</a>, and, as applicable to IRAs, the <a class="link c11n-link c11n-link--bold c11n-link--reinforced" href="https://personal.vanguard.com/pdf/s831.pdf" target="_blank">Vanguard Traditional IRA, SEP-IRA, and Roth IRA Disclosure Statement and Custodial Account Agreement</a> and <a class="link c11n-link c11n-link--bold c11n-link--reinforced" href="https://personal.vanguard.com/pdf/s821.pdf" target="_blank">5305 SEP-IRA Contribution Agreement.</a>`,
          `By clicking <b>I sign and agree</b> and <b>Submit application</b>, I also authorize the transfer of assets identified in this change of ownership application to the designated brokerage accounts.`,
        ],
      },
    ],
  },
  importantInformation: {
    header: `<b>Important information</b><br><br>`,
    content: `Vanguard Marketing Corporation is required by federal law to obtain from each person who opens an account, and in some cases, regarding each owner or trustee, certain personal information including name, street address, and taxpayer identification number, among other information-that will be used to verify identity. If you don't provide us with this information, we won't be able to open the account. If we're unable to verify your identity, Vanguard Marketing Corporation reserves the right to close your account or take other steps we deem reasonable.`,
  },
};
